import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { View } from "ui/atoms/view";
import { Button, ButtonText } from "ui/gluestack/button";
import { ReusableModal } from "ui/molecules/modal/ReusableModal";

export type ConfirmOpenParams = {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  noBackgroundClose?: boolean;
};

export interface ConfirmationModalRef {
  open: (params: ConfirmOpenParams | string) => Promise<boolean>;
  close: () => void;
}

interface Props {
  defaultConfirmText?: string;
  defaultCancelText?: string;
  className?: string;
}

export const ConfirmationModal = forwardRef<ConfirmationModalRef, Props>(
  (
    { defaultConfirmText = "Confirm", defaultCancelText = "Cancel", className },
    ref,
  ) => {
    const [showing, setShowing] = useState(false);
    const [title, setTitle] = useState<string | undefined>(undefined);
    const [message, setMessage] = useState<string>("");
    const [confirmText, setConfirmText] = useState<string>(defaultConfirmText);
    const [cancelText, setCancelText] = useState<string>(defaultCancelText);
    const [noBackgroundClose, setNoBackgroundClose] = useState<boolean>(true);

    const resolverRef = useRef<((value: boolean) => void) | null>(null);

    const resolveAndClear = (result: boolean) => {
      const resolver = resolverRef.current;
      resolverRef.current = null;
      if (resolver) resolver(result);
    };

    const handleClose = () => {
      setShowing(false);
      resolveAndClear(false);
    };

    const handleConfirm = () => {
      setShowing(false);
      resolveAndClear(true);
    };

    useImperativeHandle(ref, () => ({
      open: (params: ConfirmOpenParams | string) => {
        const p: ConfirmOpenParams =
          typeof params === "string" ? { message: params } : params;

        setTitle(p.title);
        setMessage(p.message);
        setConfirmText(p.confirmText ?? defaultConfirmText);
        setCancelText(p.cancelText ?? defaultCancelText);
        setNoBackgroundClose(p.noBackgroundClose ?? true);
        setShowing(true);

        return new Promise<boolean>((resolve) => {
          resolverRef.current = resolve;
        });
      },
      close: () => {
        if (showing) handleClose();
      },
    }));

    return (
      <ReusableModal
        isVisible={showing}
        onClose={handleClose}
        title={title ?? "Confirm action"}
        description={message}
        className={`w-full max-w-[420px] mx-auto ${className ?? ""}`}
        hasCancelButton
        noBackgroundClose={noBackgroundClose}
      >
        <View className="flex flex-row justify-end gap-x-4 ">
          <Button variant="outline" action="secondary" onPress={handleClose}>
            <ButtonText>{cancelText}</ButtonText>
          </Button>
          <Button variant="solid" action="primary" onPress={handleConfirm}>
            <ButtonText>{confirmText}</ButtonText>
          </Button>
        </View>
      </ReusableModal>
    );
  },
);

ConfirmationModal.displayName = "ConfirmationModal";

export default ConfirmationModal;
